import React, { useState, useEffect } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../../utils/apiErrors';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarMonthIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import coachAvailabilityService, {
  CalendarCoachAvailabilityDto,
  VenueDto
} from '../../services/coachAvailabilityService';
import eventService from '../../services/eventService';
import { userService } from '../../services/userService';
import { formatDateToLocal, getTodayLocal, isToday as isDateToday } from '../../utils/dateUtils';
import CoachAvailabilityCreateDialog from './CoachAvailabilityCreateDialog';
import CoachAvailabilityEditDialog from './CoachAvailabilityEditDialog';
import CoachAvailabilityDetailsDialog from './CoachAvailabilityDetailsDialog';
import CoachAvailabilityDailyTimeline from './CoachAvailabilityDailyTimeline';
import { NAV_BUTTON_TOUCH_SX } from '../Calendar/calendarA11y';

type CalendarView = 'monthly' | 'daily';

interface CoachAvailabilityCalendarProps {
  onDateSelect?: (date: string) => void;
  selectedDate?: string;
  userRole?: string;
}


interface UserDto {
  userId: string;
  name: string;
  userRoleId: string;
  userRoleName: string;
  isActive: boolean;
}

const CoachAvailabilityCalendar: React.FC<CoachAvailabilityCalendarProps> = ({
  onDateSelect,
  selectedDate,
  userRole = ''
}) => {
  const [view, setView] = useState<CalendarView>('monthly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [availabilities, setAvailabilities] = useState<CalendarCoachAvailabilityDto[]>([]);
  const [venues, setVenues] = useState<VenueDto[]>([]);
  const [coaches, setCoaches] = useState<UserDto[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>('');
  const [selectedCoach, setSelectedCoach] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState<string | null>(null);
  const [createDate, setCreateDate] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState<number>(0);

  const hungaryDayNames = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
  const hungaryMonthNames = [
    'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
    'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadAvailabilities();
  }, [currentDate, selectedVenue, selectedCoach]);

  const loadInitialData = async () => {
    try {
      const [venueData, coachData] = await Promise.all([
        eventService.getVenues(),
        userService.getCoaches()
      ]);

      setVenues(Array.isArray(venueData) ? venueData : []);
      // Map to expected UserDto format
      const mappedCoaches = Array.isArray(coachData) ? coachData.map(user => ({
        userId: user.userId,
        name: user.name,
        userRoleId: '', // We don't have this from UserListItem
        userRoleName: user.userRoleDisplayName,
        isActive: user.isActive
      })) : [];
      setCoaches(mappedCoaches);
    } catch (err) {
      console.error('Error loading initial data:', err);
    }
  };

  const loadAvailabilities = async () => {
    try {
      setLoading(true);
      setError('');

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const availabilityData = await coachAvailabilityService.getMonthAvailabilities(
        year,
        month,
        selectedCoach || undefined,
        selectedVenue || undefined
      );

      setAvailabilities(availabilityData);
    } catch (err) {
      console.error('Error loading availabilities:', err);
      setError(apiErrorMessage(err, 'Hiba történt az elérhetőségek betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1);
    const startingDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7; // Monday = 0

    const days: Date[] = [];

    // Add previous month's days to fill the first week
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Add next month's days to fill the last week
    const remainingDays = 42 - days.length; // 6 weeks * 7 days = 42 days total
    for (let day = 1; day <= remainingDays; day++) {
      days.push(new Date(year, month + 1, day));
    }

    // This generates exactly 42 days (6 weeks) which matches the service date range calculation
    return days;
  };

  const getDayAvailabilities = (date: Date): CalendarCoachAvailabilityDto[] => {
    const dateString = formatDateToLocal(date);
    return availabilities.filter(availability => availability.availabilityDate === dateString);
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    const dateString = formatDateToLocal(date);
    if (onDateSelect) {
      onDateSelect(dateString);
    }
    // Auto switch to daily view when clicking on a date
    if (view === 'monthly') {
      setView('daily');
      setCurrentDate(date);
      // Force refresh of daily view to show current data for the selected date
      forceRefreshDailyView();
    }
  };

  const handleCreateAvailability = (date: Date) => {
    setCreateDate(formatDateToLocal(date));
    setCreateDialogOpen(true);
  };

  const handleAvailabilityClick = (availabilityId: string) => {
    setSelectedAvailabilityId(availabilityId);
    setDetailsDialogOpen(true);
  };

  const handleEditAvailability = (availabilityId: string) => {
    // [Web 7 / T3, 2026-10-06] A részletező a szerkesztő megnyitásakor BEZÁR (ahogy a heti nézetben —
    // `CoachAvailabilityWeeklyView.tsx` `onEdit` — és az esemény-naptárakban is). Eddig nyitva maradt a
    // szerkesztő alatt, és a szerkesztő zárása után a RÉGI adatot mutatta: MÉRVE az E2E-n
    // (`flow-deleted-entity-edit.spec.ts`), az időközben törölt elérhetőségnél a 409 után is „Aktív"
    // státusszal látszott a már törölt sor.
    setDetailsDialogOpen(false);
    setSelectedAvailabilityId(availabilityId);
    setEditDialogOpen(true);
  };

  const handleRefresh = () => {
    loadAvailabilities();
    // Also trigger refresh for daily view if currently active
    if (view === 'daily') {
      setRefreshKey(prev => prev + 1);
    }
  };

  const forceRefreshDailyView = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleViewChange = (event: React.MouseEvent<HTMLElement>, newView: CalendarView | null) => {
    if (newView !== null) {
      setView(newView);
      // Force refresh for both views to ensure latest data is shown
      if (newView === 'daily') {
        forceRefreshDailyView();
      } else if (newView === 'monthly') {
        loadAvailabilities();
      }
    }
  };

  const handleDateChange = (date: string) => {
    setCurrentDate(new Date(date));
  };

  const canCreateAvailability = userRole === 'Coach' || userRole === 'Administrator' || userRole === 'Admin';

  const renderDay = (date: Date) => {
    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
    const isSelected = selectedDate === formatDateToLocal(date);
    const isToday = isDateToday(date);
    const dayAvailabilities = getDayAvailabilities(date);

    return (
      <Box
        key={date.toISOString()}
        sx={{
          minHeight: 120,
          border: '1px solid',
          borderColor: 'divider',
          padding: 1,
          backgroundColor: isSelected
            ? 'primary.50'
            : isToday
            ? 'warning.50'
            : !isCurrentMonth
            ? 'action.hover'
            : 'transparent',
          cursor: 'pointer',
          position: 'relative',
          opacity: isCurrentMonth ? 1 : 0.7,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => handleDateClick(date)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday ? 'bold' : 'normal',
              color: isToday
                ? 'warning.main'
                : isCurrentMonth
                  ? 'text.primary'
                  : 'text.disabled',
              opacity: isCurrentMonth ? 1 : 0.6,
            }}
          >
            {date.getDate()}
          </Typography>
          {canCreateAvailability && isCurrentMonth && view === 'daily' && (
            <IconButton
              size="small"
              aria-label="Elérhetőség hozzáadása erre a napra"
              onClick={(e) => {
                e.stopPropagation();
                handleCreateAvailability(date);
              }}
              sx={{
                opacity: 0.7,
                '&:hover': { opacity: 1 }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ maxHeight: 80, overflowY: 'auto' }}>
          {dayAvailabilities.map((availability) => (
            <Tooltip
              key={availability.availabilityId}
              title={`${availability.coachName} - ${availability.startTime.slice(0, 5)}-${availability.endTime.slice(0, 5)} - ${availability.venueNames.join(', ')}`}
            >
              <Box
                sx={{
                  mb: 0.5,
                  fontSize: '0.65rem',
                  minHeight: 32,
                  backgroundColor: 'success.main',
                  color: 'success.contrastText',
                  '&:hover': {
                    backgroundColor: 'success.dark',
                  },
                  cursor: 'pointer',
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  borderRadius: 2,
                  px: 1,
                  py: 0.5,
                  lineHeight: 1.2,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAvailabilityClick(availability.availabilityId);
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 'medium', fontSize: '0.65rem', color: 'inherit' }}>
                  {availability.coachName}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: 'inherit' }}>
                  {availability.startTime.slice(0, 5)}-{availability.endTime.slice(0, 5)}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
    );
  };

  const days = getDaysInMonth(currentDate);

  return (
    <Box>
      {view === 'monthly' ? (
        <Box>
          {/* Header with navigation and filters */}
          <Paper elevation={2} sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box display="flex" justifyContent="center" alignItems="center" mb={2} flexWrap="wrap" gap={0.5}>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
            {/* [Web UX 9 / T5, 2026-10-13] címke nélküli lapozók → aria-label + telefonon 44 px. Őr: `iconButtonLabels.guard.test.ts`. */}
            <IconButton onClick={handlePreviousMonth} size="small" aria-label="Előző hónap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronLeftIcon />
            </IconButton>

            <Typography variant="h5" sx={{
              textAlign: 'center',
              fontSize: { xs: '1rem', sm: '1.5rem' },
              whiteSpace: 'nowrap'
            }}>
              {hungaryMonthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>

            <IconButton onClick={handleNextMonth} size="small" aria-label="Következő hónap" sx={NAV_BUTTON_TOUCH_SX}>
              <ChevronRightIcon />
            </IconButton>

            <IconButton onClick={handleToday} title="Mai nap" size="small" aria-label="Mai nap" sx={NAV_BUTTON_TOUCH_SX}>
              <TodayIcon />
            </IconButton>

            <IconButton onClick={handleRefresh} title="Frissítés" size="small" aria-label="Frissítés" sx={NAV_BUTTON_TOUCH_SX}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {loading && <CircularProgress size={20} />}
        </Box>

        {/* Filters */}
        <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
            <InputLabel>Helyszín</InputLabel>
            <Select
              value={selectedVenue}
              label="Helyszín"
              onChange={(e) => setSelectedVenue(e.target.value)}
            >
              <MenuItem value="">Összes</MenuItem>
              {venues.map((venue) => (
                <MenuItem key={venue.venueId} value={venue.venueId}>
                  {venue.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'none' } }}>
            <InputLabel>Edző</InputLabel>
            <Select
              value={selectedCoach}
              label="Edző"
              onChange={(e) => setSelectedCoach(e.target.value)}
            >
              <MenuItem value="">Összes</MenuItem>
              {coaches.map((coach) => (
                <MenuItem key={coach.userId} value={coach.userId}>
                  {coach.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Legend */}
        <Box mt={2} display="flex" gap={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Jelmagyarázat:
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: 'success.main',
                borderRadius: '50%',
              }}
            />
            <Typography variant="body2">Edző elérhetősége</Typography>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Calendar Grid */}
      <Paper elevation={1} sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Box sx={{ minWidth: { xs: 500, sm: 'unset' } }}>
          {/* Day headers */}
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            {hungaryDayNames.map((day) => (
              <Box
                key={day}
                sx={{
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.grey[800]
                      : theme.palette.grey[50],
                  borderRight: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">{day}</Typography>
              </Box>
            ))}
          </Box>

          {/* Calendar days */}
          <Box display="grid" gridTemplateColumns="repeat(7, 1fr)">
            {days.map((date) => renderDay(date))}
          </Box>
        </Box>
      </Paper>
        </Box>
      ) : (
        <CoachAvailabilityDailyTimeline
          key={refreshKey}
          selectedDate={formatDateToLocal(currentDate)}
          onDateChange={handleDateChange}
          venueFilter={selectedVenue}
          coachFilter={selectedCoach}
          userRole={userRole}
        />
      )}

      {/* Floating Action Button for creating availability - available in both views */}
      {canCreateAvailability && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'sticky', bottom: 16, float: 'right', mr: 2, mt: 2, zIndex: 10 }}
          onClick={() => {
            setCreateDate(view === 'daily' ? formatDateToLocal(currentDate) : getTodayLocal());
            setCreateDialogOpen(true);
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Dialogs */}
      <CoachAvailabilityCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          loadAvailabilities();
          // Force refresh daily view if currently active
          if (view === 'daily') {
            forceRefreshDailyView();
          }
        }}
        initialDate={createDate}
        userRole={userRole}
      />

      <CoachAvailabilityEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={() => {
          setEditDialogOpen(false);
          loadAvailabilities();
          // Force refresh daily view if currently active
          if (view === 'daily') {
            forceRefreshDailyView();
          }
        }}
        availabilityId={selectedAvailabilityId}
      />

      <CoachAvailabilityDetailsDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        onEdit={handleEditAvailability}
        onDelete={() => {
          setDetailsDialogOpen(false);
          loadAvailabilities();
          // Force refresh daily view if currently active
          if (view === 'daily') {
            forceRefreshDailyView();
          }
        }}
        availabilityId={selectedAvailabilityId}
      />
    </Box>
  );
};

export default CoachAvailabilityCalendar;